import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  Chip, Collapse, Alert, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow
} from '@mui/material';
import { Add, Edit, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import dimensionService from '../../services/dimension.service';
import { useLanguage } from '../../i18n/LanguageContext';

interface DimValue { id: number; code: string; name: string; isActive: boolean; }
interface Dimension { id: number; code: string; name: string; isActive: boolean; isRequired: boolean; sortOrder: number; values: DimValue[]; }

const FinancialDimensionsTab: React.FC = () => {
  const { t } = useLanguage();
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Dimension dialog
  const [dimDialog, setDimDialog] = useState(false);
  const [dimEditId, setDimEditId] = useState<number | null>(null);
  const [dimForm, setDimForm] = useState({ code: '', name: '', isActive: true, isRequired: false, sortOrder: 0 });

  // Value dialog
  const [valDialog, setValDialog] = useState(false);
  const [valDimId, setValDimId] = useState<number | null>(null);
  const [valEditId, setValEditId] = useState<number | null>(null);
  const [valForm, setValForm] = useState({ code: '', name: '', isActive: true });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try { const res = await dimensionService.getAll(); setDimensions(res.data); }
    catch { } finally { setLoading(false); }
  };

  // Dimension CRUD
  const openDimDialog = (dim?: Dimension) => {
    if (dim) {
      setDimEditId(dim.id);
      setDimForm({ code: dim.code, name: dim.name, isActive: dim.isActive, isRequired: dim.isRequired, sortOrder: dim.sortOrder });
    } else {
      setDimEditId(null);
      setDimForm({ code: '', name: '', isActive: true, isRequired: false, sortOrder: dimensions.length });
    }
    setDimDialog(true);
  };

  const saveDim = async () => {
    try {
      if (dimEditId) await dimensionService.update(dimEditId, dimForm);
      else await dimensionService.create(dimForm);
      load(); setDimDialog(false);
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  const deleteDim = async (id: number) => {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try { await dimensionService.delete(id); load(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  // Value CRUD
  const openValDialog = (dimId: number, val?: DimValue) => {
    setValDimId(dimId);
    if (val) {
      setValEditId(val.id);
      setValForm({ code: val.code, name: val.name, isActive: val.isActive });
    } else {
      setValEditId(null);
      setValForm({ code: '', name: '', isActive: true });
    }
    setValDialog(true);
  };

  const saveVal = async () => {
    try {
      if (valEditId) await dimensionService.updateValue(valEditId, valForm);
      else if (valDimId) await dimensionService.createValue(valDimId, valForm);
      load(); setValDialog(false);
    } catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  const deleteVal = async (id: number) => {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try { await dimensionService.deleteValue(id); load(); }
    catch (err: any) { alert(err.response?.data?.message || 'Error'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('dimensions.title')}</Typography>
        <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openDimDialog()}>
          {t('dimensions.new')}
        </Button>
      </Box>

      {dimensions.length === 0 && !loading && (
        <Alert severity="info">{t('dimensions.empty')}</Alert>
      )}

      {dimensions.map(dim => (
        <Paper key={dim.id} sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
            <IconButton size="small" onClick={() => setExpanded(expanded === dim.id ? null : dim.id)}>
              {expanded === dim.id ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Chip label={dim.code} size="small" color="primary" variant="outlined" />
            <Typography sx={{ flex: 1, fontWeight: 'bold' }}>{dim.name}</Typography>
            {dim.isRequired && <Chip label={t('dimensions.required')} size="small" color="warning" />}
            <Chip label={`${dim.values?.length || 0} ${t('dimensions.values_label')}`} size="small" />
            <Chip label={dim.isActive ? t('common.active') : t('common.inactive')}
              color={dim.isActive ? 'success' : 'default'} size="small" />
            <Tooltip title={t('common.edit')}>
              <IconButton size="small" onClick={() => openDimDialog(dim)}><Edit fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <IconButton size="small" color="error" onClick={() => deleteDim(dim.id)}><Delete fontSize="small" /></IconButton>
            </Tooltip>
          </Box>
          <Collapse in={expanded === dim.id}>
            <Box sx={{ px: 2, pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">{t('dimensions.values_label')}</Typography>
                <Button size="small" startIcon={<Add />} onClick={() => openValDialog(dim.id)}>
                  {t('dimensions.new_value')}
                </Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('common.code')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('common.name')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('common.status')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dim.values || []).map(v => (
                      <TableRow key={v.id}>
                        <TableCell>{v.code}</TableCell>
                        <TableCell>{v.name}</TableCell>
                        <TableCell>
                          <Chip label={v.isActive ? t('common.active') : t('common.inactive')}
                            color={v.isActive ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => openValDialog(dim.id, v)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteVal(v.id)}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!dim.values || dim.values.length === 0) && (
                      <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', color: 'text.secondary' }}>{t('dimensions.no_values')}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </Paper>
      ))}

      {/* Dimension Dialog */}
      <Dialog open={dimDialog} onClose={() => setDimDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dimEditId ? t('dimensions.edit') : t('dimensions.new')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField fullWidth required label={t('common.code')} value={dimForm.code}
              onChange={e => setDimForm({ ...dimForm, code: e.target.value.toUpperCase() })} disabled={!!dimEditId} />
            <TextField fullWidth required label={t('common.name')} value={dimForm.name}
              onChange={e => setDimForm({ ...dimForm, name: e.target.value })} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth type="number" label={t('dimensions.sort_order')} value={dimForm.sortOrder}
              onChange={e => setDimForm({ ...dimForm, sortOrder: Number(e.target.value) })} inputProps={{ min: 0 }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControlLabel control={<Switch checked={dimForm.isRequired}
              onChange={e => setDimForm({ ...dimForm, isRequired: e.target.checked })} />}
              label={t('dimensions.required')} />
            <FormControlLabel control={<Switch checked={dimForm.isActive}
              onChange={e => setDimForm({ ...dimForm, isActive: e.target.checked })} />}
              label={t('common.active')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDimDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={saveDim} disabled={!dimForm.code || !dimForm.name}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Value Dialog */}
      <Dialog open={valDialog} onClose={() => setValDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{valEditId ? t('dimensions.edit_value') : t('dimensions.new_value')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField fullWidth required label={t('common.code')} value={valForm.code}
              onChange={e => setValForm({ ...valForm, code: e.target.value.toUpperCase() })} disabled={!!valEditId} />
            <TextField fullWidth required label={t('common.name')} value={valForm.name}
              onChange={e => setValForm({ ...valForm, name: e.target.value })} />
          </Box>
          <FormControlLabel sx={{ mt: 2 }} control={<Switch checked={valForm.isActive}
            onChange={e => setValForm({ ...valForm, isActive: e.target.checked })} />}
            label={t('common.active')} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={saveVal} disabled={!valForm.code || !valForm.name}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FinancialDimensionsTab;
